import {
  Component,
  OnInit,
  OnDestroy,
  HostBinding,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  ViewChild
} from "@angular/core";
import { AdvQueueService } from "@onsip/common/services/api/resources/queue/adv-queue.service";
import { Validators, AbstractControl, NonNullableFormBuilder } from "@angular/forms";
import { BehaviorSubject, Subscription } from "rxjs";
import { SnackbarService } from "../../../../../shared/components/snackbar/snackbar.service";
import { TranslateService } from "@ngx-translate/core";
import { take, filter, map } from "rxjs/operators";
import { MatDialog } from "@angular/material/dialog";
import { ModalMaterialComponent } from "../../../../../shared/components/modal/modal-material.component";
import { QueueWithExt } from "../../../acdQueuesPage/acd-queues-page.component";
import { AppAddressService } from "@onsip/common/services/api/resources/appAddress/app-address.service";
import { AccountResourceService } from "@onsip/common/services/api/resources/package/account-resource.service";
import { ExtensionsListComponent } from "@onsip/web/features/shared/components/extensionsList/extensions-list.component";
import { E2eLocators } from "@onsip/e2e/configs/locators";
import { NAME_VALIDATOR_REGEX } from "@onsip/common/libraries/validators/name-validators";

@Component({
  selector: "onsip-acd-queues-basic-edit",
  templateUrl: "./acd-queues-basic-settings-edit.component.html",
  styleUrls: ["./acd-queues-basic-settings-edit.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AcdQueuesBasicEditComponent implements OnInit, OnDestroy {
  @HostBinding("class.onsip-grid-content")
  @HostBinding("class.onsip-admin-host-grid")
  _dontUse = true;
  @Input() queue!: QueueWithExt;
  @Output() viewPanel = new EventEmitter<void>();
  @ViewChild("queueExtensionsList") queueExtensionsListRef!: ExtensionsListComponent;
  @ViewChild("loginExtensionsList") loginExtensionsListRef!: ExtensionsListComponent;
  loadingStateComplete$ = new BehaviorSubject(true);

  queueInfo = this.fb.group({
    queueName: ["", [Validators.required, Validators.pattern(NAME_VALIDATOR_REGEX)]],
    queueSipAddress: [""],
    loginSipAddress: [""],
    loginPassword: ["", Validators.required],
    loginEmail: [""],
    maxCallers: ["", Validators.required],
    maxAgents: ["", Validators.required],
    queueExtensions: [<Array<string>>[]],
    loginExtensions: [<Array<string>>[]],
    loginPhoneNumbers: [<Array<string>>[]]
  });
  oneColumn = false;
  twoColumn = false;
  threeColumn = false;
  queueAddressId = "";
  loginAddressId = "";
  loginDidIds: Array<string> = [];
  phonesList: Array<string> = [];
  agentPricingText = "";
  extHint = "";
  isMobile = false;
  E2eLocators = E2eLocators;

  private unsubscriber = new Subscription();

  constructor(
    private queueService: AdvQueueService,
    private snackbarService: SnackbarService,
    private translate: TranslateService,
    private dialog: MatDialog,
    private appAddress: AppAddressService,
    private resourceService: AccountResourceService,
    private cdRef: ChangeDetectorRef,
    private fb: NonNullableFormBuilder
  ) {}

  ngOnInit() {
    this.setFormValues();
    this.getAgentPricingText();
  }

  ngOnDestroy(): void {
    this.unsubscriber.unsubscribe();
  }

  setFormValues(): void {
    this.queueInfo.controls.queueName.setValue(this.queue.queueName);
    this.queueInfo.controls.queueSipAddress.setValue(
      this.queue.appAddress.substring(0, this.queue.appAddress.indexOf("@"))
    );
    this.queueInfo.controls.maxCallers.setValue(this.queue.maxCaller);
    this.queueInfo.controls.maxAgents.setValue(this.queue.maxAgent);
    this.queueInfo.controls.queueExtensions.setValue([...this.queue.queueExtensions]);
    this.queueInfo.controls.loginExtensions.setValue([...this.queue.loginExtensions]);
    this.queueInfo.controls.loginPassword.setValue(this.queue.password);
    this.queueInfo.controls.loginSipAddress.setValue(this.queue.loginSipAddress);
    this.setLoginForms();
  }

  setLoginForms(): void {
    this.unsubscriber.add(
      this.appAddress.orgState.subscribe(appAddresses => {
        const queueAndLoginAppAddresses = appAddresses.filter(
          appAdd =>
            appAdd.appId === this.queue.advQueueLoginAppId || appAdd.appId === this.queue.appId
        );
        queueAndLoginAppAddresses.map(appAdd => {
          if (appAdd.appId === this.queue.appId) {
            this.queueAddressId = appAdd.addressId;
          } else if (appAdd.appId === this.queue.advQueueLoginAppId) {
            this.loginAddressId = appAdd.addressId;
            this.queueInfo.controls.loginSipAddress.setValue(appAdd.address.username);
          }
        });
      })
    );
  }

  getAgentPricingText(): void {
    this.unsubscriber.add(
      this.resourceService.state
        .pipe(
          filter(state => !state.loading),
          map(state => {
            return Object.values(state.state);
          })
        )
        .pipe(
          map(resourcesArray => {
            return resourcesArray.find(res => res.name === "SimpleQueue");
          })
        )
        .subscribe(res => {
          this.agentPricingText = `$${res?.monthlyRecurringCharge || "undeterminded"}/agent`;
          this.cdRef.markForCheck();
        })
    );
  }

  togglePanel(): void {
    this.unsubscriber.add(
      this.dialog
        .open(ModalMaterialComponent, {
          panelClass: ["mat-typography", "onsip-dialog-universal-style"],
          data: {
            title: this.translate.instant("ONSIP_I18N.CANCEL_EDITS"),
            message: this.translate.instant(
              "ONSIP_I18N.ANY_CHANGES_YOU_HAVE_MADE_WILL_NOT_BE_SAVED"
            ),
            primaryBtnText: this.translate.instant("ONSIP_I18N.OK"),
            secondaryBtnText: this.translate.instant("ONSIP_I18N.KEEP_EDITING"),
            primaryBtnFlat: true
          }
        })
        .afterClosed()
        .pipe(take(1))
        .subscribe(res => {
          if (res && res.doPrimaryAction) this.viewPanel.emit();
        })
    );
  }

  showSaveSnackbar(): void {
    this.snackbarService.openSnackBar(
      this.translate.instant("ONSIP_I18N.YOUR_CHANGES_HAVE_BEEN_SAVED"),
      "success"
    );
  }

  showExtSaveSnackbar(): void {
    this.snackbarService.openSnackBar(
      this.translate.instant("Extensions have been saved."),
      "success"
    );
  }

  showLoginSaveSnackbar(): void {
    this.snackbarService.openSnackBar(
      this.translate.instant("Login phone numbers have been saved."),
      "success"
    );
  }

  showErrorSnackbar(message: string): void {
    this.snackbarService.openSnackBar(message, "error");
  }

  cancel(): void {
    const modal = this.dialog.open(ModalMaterialComponent, {
      panelClass: ["mat-typography", "onsip-dialog-universal-style"],
      data: {
        title: this.translate.instant("ONSIP_I18N.CANCEL_EDITS"),
        message: this.translate.instant("ONSIP_I18N.ANY_CHANGES_YOU_HAVE_MADE_WILL_NOT_BE_SAVED"),
        primaryBtnText: this.translate.instant("ONSIP_I18N.OK"),
        secondaryBtnText: this.translate.instant("ONSIP_I18N.KEEP_EDITING"),
        primaryBtnFlat: true
      }
    });

    this.unsubscriber.add(
      modal
        .afterClosed()
        .pipe(take(1))
        .subscribe(retObj => {
          if (retObj && retObj.doPrimaryAction) {
            this.viewPanel.emit();
          }
        })
    );
  }

  handleSave(): void {
    this.loadingStateComplete$.next(false);
    if (
      this.queueInfo.controls.queueSipAddress.value !==
      this.queue.appAddress.substring(0, this.queue.appAddress.indexOf("@"))
    ) {
      this.editSipAddress(this.queue.appAddress, this.queueInfo.controls.queueSipAddress.value);
    }
    if (this.queueInfo.controls.loginSipAddress.value !== this.queue.loginSipAddress) {
      this.editSipAddress(
        this.queue.loginSipAddress +
          this.queue.appAddress.substring(this.queue.appAddress.indexOf("@")),
        this.queueInfo.controls.loginSipAddress.value
      );
    }
    this.queueService
      .advQueueEdit({
        AdvQueueId: this.queue.advQueueId,
        QueueName: this.queueInfo.controls.queueName.value,
        MaxCaller: this.queueInfo.controls.maxCallers.value,
        MaxAgent: this.queueInfo.controls.maxAgents.value,
        CallerTimeout: this.queue.callerTimeout,
        AgentTimeout: this.queue.agentTimeout,
        RingStrategy: this.queue.ringStrategy,
        WrapupTime: this.queue.wrapupTime,
        MemberDelay: this.queue.memberDelay,
        AnnounceFrequency: this.queue.announceFrequency,
        Username:
          this.queueInfo.controls.loginSipAddress.value +
          this.queue.appAddress.substring(this.queue.appAddress.indexOf("@")),
        Password: this.queueInfo.controls.loginPassword.value,
        SmartQueue: this.queue.smartQueue,
        AnnounceHoldTime: this.queue.announceHoldTime,
        ReportHoldTime: this.queue.reportHoldTime,
        EscapeAddress: this.queue.escapeAddress,
        FailoverAddress: this.queue.failoverAddress,
        MusicSourceId: this.queue.musicSourceId,
        StorageServiceId: this.queue.storageServiceId,
        AnnounceRecording: this.queue.announceRecording,
        RecordingTone: this.queue.recordingTone
      })
      .then(result => {
        if (result.status === "success") {
          if (this.queueInfo.controls.queueExtensions.value !== this.queue.queueExtensions) {
            this.queueExtensionsListRef.editExtensions(
              this.queueInfo.controls.queueSipAddress.value
            );
          }
          if (this.queueInfo.controls.loginExtensions.value !== this.queue.loginExtensions) {
            this.loginExtensionsListRef.editExtensions(
              this.queueInfo.controls.loginSipAddress.value
            );
          }
          this.showSaveSnackbar();
          this.queueService.advQueueBrowse();
          this.viewPanel.emit();
          this.loadingStateComplete$.next(true);
        } else {
          this.showErrorSnackbar(result.data.message);
          this.queueService.clearErrors();
          this.loadingStateComplete$.next(true);
        }
      });
  }

  editSipAddress(oldAddress: string, newUserName: string): void {
    const params = {
      Username: newUserName
    };
    this.appAddress.appAddressEdit(oldAddress, params).then(result => {
      if (result.status === "success") {
        this.showSaveSnackbar();
      } else {
        this.showErrorSnackbar(result.data.message);
        this.appAddress.clearErrors();
      }
    });
  }

  getGenericError(control: AbstractControl, value: string): string {
    return control.hasError("required")
      ? this.translate.instant("ONSIP_I18N._VALUE__IS_REQUIRED", { value })
      : control.hasError("maxlength")
      ? this.translate.instant("ONSIP_I18N.VALUE_IS_LONGER_THAN_LENGTH_CHARACTERS", {
          value,
          length: control.errors?.maxlength.requiredLength
        })
      : control.hasError("pattern")
      ? this.translate.instant(
          "ONSIP_I18N._VALUE__CAN_ONLY_CONTAIN_LETTERS_SPACES_QUOTES_OR_DASHES",
          { value }
        )
      : "";
  }

  getGenericRequiredError(control: AbstractControl | undefined | null, value: string): string {
    // eslint-disable-next-line no-null/no-null
    if (control === undefined || control === null) return "";
    return control.hasError("required")
      ? this.translate.instant("ONSIP_I18N._VALUE__IS_REQUIRED", { value })
      : "";
  }
}
